/**
 * Detail-view utilities for reentry planning (plan detail card, sections, widgets, and the
 * Honest Jobs adapters).
 *
 * List/access/location helpers live in reentry-planning.util.js; section write access, the
 * Active/Archived split, and section list transformations live in case-plan-section.util.js.
 */

import {
  CASE_PLAN_SECTION_STATUSES,
  HONEST_JOBS_JOB_DETAIL_FIELD_KEYS,
  HONEST_JOBS_RESOURCE_DETAIL_FIELD_KEYS,
  REENTRY_PLAN_TYPES,
  toVisibilityPlanType,
} from '../constants/reentry-planning.constants';

// --- honest-jobs-format.util.js ---
/** Removes complete HTML tags (`<...>` with ≥1 char inside) in linear time (ReDoS-safe). */
const stripRemainingHtmlTags = (text) => {
  let result = '';
  let index = 0;

  while (index < text.length) {
    if (text[index] === '<') {
      const closeIndex = text.indexOf('>', index + 1);
      if (closeIndex > index + 1) {
        index = closeIndex + 1;
        continue;
      }
    }

    result += text[index];
    index += 1;
  }

  return result;
};

/** Strips HTML tags and normalizes rich-text the same way as the Honest Jobs PDF exporter. */
export const stripHtmlTags = (html) =>
  stripRemainingHtmlTags(
    String(html ?? '')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<\/li>/gi, '\n')
      .replace(/<li>/gi, '• ')
  )
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\r/g, '')
    .trim();

/** Formats a reentry-plan address object into a single display line. */
export const formatReentryPlanAddress = (address = {}) => {
  const line1 = [address.addressLine1, address.addressLine2].filter(Boolean).join(', ');
  const cityStateZip = [address.city, address.county, address.state, address.postalCode]
    .filter(Boolean)
    .join(', ');
  return [line1, cityStateZip].filter(Boolean).join(', ');
};

// --- map-reentry-plan-job.util.js ---
/** PayFrequency values from Honest Jobs upstream (.NET enum). */
const UNKNOWN_PAY_FREQUENCY = 'Unknown';

const PAY_FREQUENCY_PERIOD_LABELS = {
  Hourly: 'hour',
  Monthly: 'month',
  Yearly: 'year',
  Daily: 'day',
  Weekly: 'week',
};

const PAY_RATE_DOE_LABEL = 'Depends on experience';

/** PayRateDisplayOption values from Honest Jobs upstream (.NET enum). */
const PAY_RATE_DISPLAY_OPTION = {
  FIXED: 'Fixed',
  STARTING_AT: 'StartingAt',
  UP_TO: 'UpTo',
  RANGE: 'Range',
  DOE: 'DOE',
  UNKNOWN: 'Unknown',
  WAGE_PLUS_TIP: 'WagePlusTip',
};

const formatJobPayFrequencySuffix = (frequency) => {
  if (!frequency || frequency === UNKNOWN_PAY_FREQUENCY) {
    return '';
  }

  const periodLabel = PAY_FREQUENCY_PERIOD_LABELS[frequency];
  if (periodLabel) {
    return ` per ${periodLabel}`;
  }

  return ` per ${frequency.toLowerCase()}`;
};

const formatFixedPayRate = (payRate, maxPayRate, frequencySuffix) => {
  if (payRate > 0) {
    return `$${payRate}${frequencySuffix}`;
  }

  if (maxPayRate > 0) {
    return `$${maxPayRate}${frequencySuffix}`;
  }

  return '';
};

const formatJobPayRate = (job = {}) => {
  const payRate = Number(job.payRate ?? 0);
  const maxPayRate = Number(job.maxPayRate ?? 0);
  const payRateDisplayOption = String(job.payRateDisplayOption ?? '');
  const frequencySuffix = formatJobPayFrequencySuffix(job.frequency);

  switch (payRateDisplayOption) {
    case PAY_RATE_DISPLAY_OPTION.RANGE:
      if (payRate > 0 && maxPayRate > payRate) {
        return `$${payRate} - $${maxPayRate}${frequencySuffix}`;
      }
      return '';

    case PAY_RATE_DISPLAY_OPTION.STARTING_AT:
      return payRate > 0 ? `Starting at $${payRate}${frequencySuffix}` : '';

    case PAY_RATE_DISPLAY_OPTION.UP_TO:
      if (maxPayRate > 0) {
        return `Up to $${maxPayRate}${frequencySuffix}`;
      }
      return payRate > 0 ? `Up to $${payRate}${frequencySuffix}` : '';

    case PAY_RATE_DISPLAY_OPTION.DOE:
      return PAY_RATE_DOE_LABEL;

    case PAY_RATE_DISPLAY_OPTION.WAGE_PLUS_TIP:
      return payRate > 0 ? `$${payRate}${frequencySuffix} + tips` : 'Wage + tips';

    case PAY_RATE_DISPLAY_OPTION.UNKNOWN:
      return '';

    case PAY_RATE_DISPLAY_OPTION.FIXED:
    default:
      return formatFixedPayRate(payRate, maxPayRate, frequencySuffix);
  }
};

const formatJobEducationLevel = (job = {}) => {
  if (typeof job.educationLevel === 'string') {
    return job.educationLevel;
  }

  return '';
};

const formatJobFrequency = (frequency) =>
  frequency === UNKNOWN_PAY_FREQUENCY ? '' : (frequency ?? '');

const buildJobDetailFields = (job, strippedDescription) => ({
  'Pay Rate': formatJobPayRate(job),
  Frequency: formatJobFrequency(job.frequency),
  'Education Level': formatJobEducationLevel(job),
  Description: strippedDescription,
});

/**
 * Maps a reentry-plan `jobs[]` item to the Honest Jobs widget `IResource` shape.
 *
 * @param {*} job
 */
export const mapReentryPlanJobToHonestJobsResource = (job) => {
  const strippedDescription = stripHtmlTags(job?.description);

  return {
    name: job?.title ?? '',
    providerName: job?.companyName ?? '',
    description: strippedDescription,
    address: formatReentryPlanAddress(job?.address),
    details: buildJobDetailFields(job, strippedDescription),
  };
};

// --- map-reentry-plan-resource.util.js ---
const getStepAction = (step) => step?.action ?? '';
const getStepContact = (step) => step?.contact ?? '';

const combineNextStepParts = (action, contact) => {
  if (!action) {
    return contact;
  }
  if (!contact) {
    return action;
  }
  return `${action}: ${contact}`;
};

const formatSingleNextStep = (step) =>
  combineNextStepParts(getStepAction(step), getStepContact(step));

const formatNextSteps = (steps = []) =>
  steps.map(formatSingleNextStep).filter(Boolean).join('\n');

const getProgramRules = (programDetails) =>
  Array.isArray(programDetails.rules) ? programDetails.rules : [];

const getSupportedLanguages = (programDetails) =>
  Array.isArray(programDetails.supported_languages) ? programDetails.supported_languages : [];

const getServiceTags = (programDetails) =>
  Array.isArray(programDetails.service_tags) ? programDetails.service_tags : [];

const getProgramDetailField = (programDetails, fieldName) => programDetails[fieldName] ?? '';

/** Formats service_tags as a bullet list — matches PDF `formatWeOfferTags`. */
const formatWeOffer = (serviceTags) =>
  serviceTags
    .map((tag) => String(tag).trim())
    .filter((tag) => tag !== '')
    .map((tag) => `• ${tag}`)
    .join('\n');

/** Formats rules containing "must" — matches PDF `formatRequired`. */
const formatRequired = (rules) =>
  rules
    .filter((rule) => String(rule).toLowerCase().includes('must'))
    .map((rule) => String(rule).trim())
    .join('\n\n');

const buildResourceDetailFields = (programDetails, strippedDescription) => {
  const rules = getProgramRules(programDetails);
  const supportedLanguages = getSupportedLanguages(programDetails);

  return {
    Eligibility: rules.join('\n'),
    Availability: getProgramDetailField(programDetails, 'availability'),
    Description: strippedDescription,
    'We offer': formatWeOffer(getServiceTags(programDetails)),
    Languages: supportedLanguages.join(', '),
    Cost: getProgramDetailField(programDetails, 'free_or_reduced'),
    'Next Steps': formatNextSteps(programDetails.next_steps),
    Website: getProgramDetailField(programDetails, 'website_url'),
    Facebook: getProgramDetailField(programDetails, 'facebook_url'),
    'Coverage Area': getProgramDetailField(programDetails, 'coverage_description'),
    Required: formatRequired(rules),
    'Last Reviewed': getProgramDetailField(programDetails, 'validation_date'),
  };
};

/**
 * Maps a reentry-plan `resources[]` item to the Honest Jobs widget `IResource` shape.
 *
 * @param {HonestJobsReentryPlanResource} resource
 */
export const mapReentryPlanResourceToHonestJobsResource = (resource) => {
  const programDetails = resource?.programDetails ?? {};
  const strippedDescription = stripHtmlTags(programDetails.description);

  return {
    name: resource?.programName ?? programDetails.name ?? '',
    providerName: resource?.providerName ?? programDetails.provider_name ?? '',
    description: strippedDescription,
    address: formatReentryPlanAddress(resource?.address),
    details: buildResourceDetailFields(programDetails, strippedDescription),
  };
};

// --- honest-jobs-widget.util.js ---
export const HONEST_JOBS_WIDGET_TYPE = 'honest-jobs';
const HONEST_JOBS_RESOURCE_TYPE = 'Honest Jobs';
const HONEST_JOBS_SOURCE = 'honest_jobs';
const HONEST_JOBS_JOBS_WIDGET_TYPE = 'honest_jobs_jobs';

const EMPTY_REENTRY_PLAN_ITEMS = Object.freeze({
  jobs: [],
  resourcesByCategory: {},
});

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const readWidgetMetadata = (widget) =>
  isPlainObject(widget?.metadata) ? widget.metadata : {};

/** `widget_type.metadata` flag. Absent/undefined is treated as false. */
const WIDGET_TYPE_METADATA_DESCRIPTION_EDITABLE = 'description_editable';

export const isWidgetDescriptionEditable = (widget) =>
  readWidgetMetadata(widget)[WIDGET_TYPE_METADATA_DESCRIPTION_EDITABLE] === true;

export const isHonestJobsWidget = (widget) =>
  readWidgetMetadata(widget).source === HONEST_JOBS_SOURCE;

export const widgetRequiresReentryPlan = (widget) => isHonestJobsWidget(widget);

const readHonestJobsSourceKey = (widget) => {
  const sourceKey = readWidgetMetadata(widget).source_key;
  return typeof sourceKey === 'string' && sourceKey.trim() !== '' ? sourceKey : '';
};

const normalizeCategoryKey = (value) => String(value ?? '').trim().toLowerCase();

const getGroupJobs = (group) => (Array.isArray(group?.jobs) ? group.jobs : []);

const getGroupResources = (group) =>
  Array.isArray(group?.resources) ? group.resources : [];

const appendItemsToCategoryMap = (categoryMap, categoryName, items) => {
  const categoryKey = normalizeCategoryKey(categoryName);
  if (!categoryKey || items.length === 0) {
    return categoryMap;
  }

  const existingItems = categoryMap[categoryKey] ?? [];
  return { ...categoryMap, [categoryKey]: [...existingItems, ...items] };
};

/** Collects reentry-plan jobs and category-keyed resources from group arrays. */
export const buildReentryPlanItems = (reentryPlan) => {
  if (!Array.isArray(reentryPlan?.groups)) {
    return EMPTY_REENTRY_PLAN_ITEMS;
  }

  return reentryPlan.groups.reduce(
    (items, group) => ({
      jobs: [
        ...items.jobs,
        ...getGroupJobs(group).map(mapReentryPlanJobToHonestJobsResource),
      ],
      resourcesByCategory: appendItemsToCategoryMap(
        items.resourcesByCategory,
        group?.name,
        getGroupResources(group).map(mapReentryPlanResourceToHonestJobsResource),
      ),
    }),
    EMPTY_REENTRY_PLAN_ITEMS,
  );
};

const resolveHonestJobsTitle = (widget) =>
  typeof widget?.displayName === 'string' ? widget.displayName : '';

const resolveHonestJobsResources = (config) =>
  Array.isArray(config.resources) ? config.resources : [];

const isHonestJobsJobsWidget = (widget) => widget?.widgetType === HONEST_JOBS_JOBS_WIDGET_TYPE;

const resolveResourcesForCategory = (resourcesByCategory, categoryValue) => {
  const categoryKey = normalizeCategoryKey(categoryValue);
  if (!categoryKey) {
    return [];
  }

  const resources = resourcesByCategory[categoryKey];
  return Array.isArray(resources) ? resources : [];
};

const resolveHonestJobsResourcesFromReentryPlan = (
  widget,
  reentryPlanItems = EMPTY_REENTRY_PLAN_ITEMS,
) => {
  const jobs = reentryPlanItems.jobs ?? [];
  const resourcesByCategory = reentryPlanItems.resourcesByCategory ?? {};
  const sourceKey = readHonestJobsSourceKey(widget);

  let reentryResources = [];
  if (isHonestJobsJobsWidget(widget)) {
    reentryResources = jobs;
  } else if (sourceKey !== '') {
    reentryResources = resolveResourcesForCategory(resourcesByCategory, sourceKey);
  }

  if (reentryResources.length > 0) {
    return reentryResources;
  }

  const config = isPlainObject(widget?.config) ? widget.config : {};
  return resolveHonestJobsResources(config);
};

const pickTruthyConfigField = (config, key) => (config[key] ? { [key]: config[key] } : {});

const pickNullableNumberConfigField = (config, key) =>
  config[key] != null ? { [key]: config[key] } : {};

const pickHonestJobsOptionalConfig = (config) => ({
  ...pickNullableNumberConfigField(config, 'initialVisibleCount'),
  ...pickTruthyConfigField(config, 'moreInfoLabel'),
  ...pickTruthyConfigField(config, 'emptyStateTitle'),
  ...pickTruthyConfigField(config, 'emptyStateDescription'),
});

const resolveHonestJobsResourceOrJobDetailFieldKeys = (widget, config) => {
  if (Array.isArray(config.detailFieldKeys)) {
    return config.detailFieldKeys;
  }

  return isHonestJobsJobsWidget(widget)
    ? HONEST_JOBS_JOB_DETAIL_FIELD_KEYS
    : HONEST_JOBS_RESOURCE_DETAIL_FIELD_KEYS;
};

export const mapHonestJobsWidgetProps = (
  widget,
  reentryPlanItems = EMPTY_REENTRY_PLAN_ITEMS,
) => {
  const config = isPlainObject(widget?.config) ? widget.config : {};

  return {
    title: resolveHonestJobsTitle(widget),
    resources: resolveHonestJobsResourcesFromReentryPlan(widget, reentryPlanItems),
    resourceType: config.resourceType ?? HONEST_JOBS_RESOURCE_TYPE,
    detailFieldKeys: resolveHonestJobsResourceOrJobDetailFieldKeys(widget, config),
    ...pickHonestJobsOptionalConfig(config),
  };
};

// --- case-plan-widget.util.js ---
export const UNKNOWN_WIDGET_TYPE = 'unknown';

const WIDGET_MAPPERS = [
  {
    matches: isHonestJobsWidget,
    type: HONEST_JOBS_WIDGET_TYPE,
    mapProps: mapHonestJobsWidgetProps,
  },
];

const resolveWidgetMapper = (widget) =>
  WIDGET_MAPPERS.find((entry) => entry.matches(widget)) ?? null;

export const mapWidget = (widget, reentryPlanItems = {}) => {
  const mapper = resolveWidgetMapper(widget);
  const chrome = {
    id: widget.id,
    description: widget.description ?? '',
    descriptionEditable: isWidgetDescriptionEditable(widget),
  };

  if (!mapper) {
    return { ...chrome, type: UNKNOWN_WIDGET_TYPE, props: {} };
  }

  return {
    ...chrome,
    type: mapper.type,
    props: mapper.mapProps(widget, reentryPlanItems),
  };
};

export const requiresReentryPlanFetch = (sections = []) =>
  sections.some((section) =>
    (section.widgets ?? []).some((widget) => widgetRequiresReentryPlan(widget)),
  );

// --- case-plan-detail.util.js ---
const DEFAULT_LEARNER_NAME = '-';

const buildPersonName = (person) => {
  const firstName = person.firstName ? person.firstName : '';
  const lastName = person.lastName ? person.lastName : '';
  return `${firstName} ${lastName}`.trim();
};

const resolveLearnerDisplayName = (name, planName) => {
  if (name) {
    return name;
  }
  return planName ? planName : DEFAULT_LEARNER_NAME;
};

const formatLearnerName = (person, planName) => {
  if (!person) {
    return resolveLearnerDisplayName('', planName);
  }

  return resolveLearnerDisplayName(buildPersonName(person), planName);
};

export const mapDetailCardProps = (plan) => {
  const person = plan?.person;

  return {
    learnerName: formatLearnerName(person, plan?.name),
    username: person?.username ?? '',
    docId: person?.docId ?? plan?.docId ?? '',
    location: person?.location?.locationName ?? '',
    showBackButton: true,
  };
};

/**
 * Maps one API section into UI props.
 *
 * `visibleToLearner` defaults to **false**: the shared section component defaults the toggle
 * to true, so an absent flag must never be read as "visible to the learner".
 */
const mapSection = (section, reentryPlanItems = {}) => ({
  id: section.id,
  title: section.name ?? '',
  description: section.description ?? '',
  visibleToLearner: section.visibleToLearner ?? false,
  status: section.status ?? CASE_PLAN_SECTION_STATUSES.ACTIVE,
  sortOrder: section.sortOrder ?? 0,
  widgets: (section.widgets ?? []).map((widget) => mapWidget(widget, reentryPlanItems)),
});

/** Maps API sections into UI props, preserving backend sort order. */
export const mapSections = (sections = [], reentryPlanItems = {}) =>
  sections.map((section) => mapSection(section, reentryPlanItems));

/**
 * Maps a section mutation response. Widgets are untouched by every section mutation, so the
 * caller keeps the widgets it already mapped and only a freshly created (empty) section maps
 * the response's own list.
 */
export const mapSectionMutationResponse = (section) => mapSection(section ?? {});

const resolvePlanType = (plan) => plan?.planType ?? plan?.plan_type;

const isReentryPreviewPlanType = (plan) =>
  toVisibilityPlanType(resolvePlanType(plan)) === REENTRY_PLAN_TYPES.PREVIEW.feature;

/**
 * Editor only when the API sends isEditor === true.
 * Omitted or false keeps the status chip read-only (All Plans viewers).
 */
const resolveIsEditor = (plan) => plan?.isEditor === true;

const resolvePlanId = (plan) => plan?.id ?? plan?.planId ?? null;

/**
 * Plan fields the detail view needs beyond the header card: plan status, plan type, and
 * `isEditor` membership of *this* plan. Together they decide section write access.
 */
const mapPlanSummary = (plan) => ({
  id: plan?.id ?? null,
  status: plan?.status ?? null,
  planType: resolvePlanType(plan) ?? null,
  isEditor: resolveIsEditor(plan),
  isWritablePlanType: !isReentryPreviewPlanType(plan),
});

export const mapCasePlanDetailResponse = (response, reentryPlan = null) => {
  const reentryPlanItems = buildReentryPlanItems(reentryPlan);
  const plan = response?.plan;

  return {
    cardProps: mapDetailCardProps(plan),
    plan: mapPlanSummary(plan),
    sections: mapSections(response?.sections, reentryPlanItems),
    isViewOnly: isReentryPreviewPlanType(plan),
    status: plan?.status ?? null,
    planId: resolvePlanId(plan),
    isEditor: resolveIsEditor(plan),
  };
};

// --- case-plan-detail-loader.util.js ---
export const fetchReentryPlanIfNeeded = async (sections, personId, fetchLearnerReentryPlan) => {
  if (!requiresReentryPlanFetch(sections) || !personId) {
    return null;
  }

  try {
    return await fetchLearnerReentryPlan(personId);
  } catch {
    return null;
  }
};

export const loadCasePlanDetailData = async (
  casePlanId,
  { fetchCasePlanDetail, fetchLearnerReentryPlan, signal },
) => {
  const response = await fetchCasePlanDetail(casePlanId, signal);
  const personId = response?.plan?.person?.personId;
  const reentryPlan = await fetchReentryPlanIfNeeded(
    response?.sections,
    personId,
    (learnerPersonId) => fetchLearnerReentryPlan(learnerPersonId, signal),
  );

  return mapCasePlanDetailResponse(response, reentryPlan);
};

export const updateWidgetDescription = (sections, widgetId, description) =>
  sections.map((section) => ({
    ...section,
    widgets: (section.widgets ?? []).map((widget) =>
      widget.id === widgetId ? { ...widget, description } : widget,
    ),
  }));
